import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getUserTimezone, parseGameDateTime } from "@/v2/pages/Events/date-utils";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * "Today" / "Tomorrow" when `gameDateTime`'s calendar date - in the
 * viewer's own timezone, the same zone `formatGameTime` renders the clock
 * time in - matches, otherwise "" so the caller falls back to a formatted
 * date. Comparing in the viewer's zone (rather than ET) keeps the label
 * consistent with the time it's concatenated with: near ET midnight a
 * viewer west of ET (or far enough east, e.g. IST) can have a local
 * calendar date that differs from the ET date. Uses calendar-day
 * arithmetic (not a fixed 24h offset) so it stays correct across DST
 * transitions.
 */
export function relativeDayLabel(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  const parsed = parseGameDateTime(gameDateTime);
  if (!parsed.isValid()) return "";

  const userTz = getUserTimezone();
  const gameDay = parsed.tz(userTz).format("YYYY-MM-DD");
  const todayLocal = dayjs().tz(userTz);
  if (gameDay === todayLocal.format("YYYY-MM-DD")) return "Today";
  if (gameDay === todayLocal.add(1, "day").format("YYYY-MM-DD")) return "Tomorrow";
  return "";
}

/**
 * Coarse "8m ago" / "3h ago" / "2d ago" relative label for a timestamp.
 * Shared by the Highlights row (clip age) and the Spotlights row (channel
 * `updatedAt`) so the two read identically.
 */
export function timeAgo(dateStr?: string): string {
  if (!dateStr) return "";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
